import React, { useState, useRef, useEffect } from 'react';

const KB = {
  services: `We offer 8 core services:\n• Web Development\n• App Development\n• Digital Marketing\n• SEO Optimization\n• UI/UX Design\n• Branding\n• Video Editing\n• Social Media Management\n\nIf you want, I can suggest a package based on your goal (leads, sales, app growth, or brand awareness).`,
  web: `Web Development (what we deliver):\n• Business websites & landing pages\n• E-commerce stores\n• Admin dashboards & custom portals\n• Speed, SEO, and mobile optimization\n\nTypical timeline: 2-8 weeks (scope dependent).`,
  app: `App Development:\n• Android/iOS apps (native or cross-platform)\n• User auth, payments, APIs, admin panel\n• App store deployment support\n\nTypical timeline: 6-14 weeks based on features.`,
  seo: `SEO Optimization:\n• Technical SEO audit + fixes\n• On-page SEO & content mapping\n• Internal linking + authority plan\n• Monthly ranking & traffic reporting\n\nBest for long-term organic growth.`,
  marketing: `Digital Marketing:\n• Google / Meta / LinkedIn campaigns\n• Funnel setup + conversion tracking\n• Creative testing + retargeting\n• Weekly performance optimization\n\nGoal: lower CPA and increase qualified leads.`,
  design: `UI/UX Design:\n• UX research + wireframes\n• High-fidelity UI systems\n• Prototype testing\n• Design handoff for dev teams\n\nWorks for websites, apps, and SaaS dashboards.`,
  branding: `Branding:\n• Logo & visual identity\n• Brand color/type system\n• Social/media brand kit\n• Tone, messaging, positioning support\n\nGreat for rebranding or new launches.`,
  video: `Video Editing:\n• Reels/shorts/ad creatives\n• Explainers and product videos\n• Motion graphics + subtitles + hooks\n• Platform-specific optimization\n\nBuilt to improve watch time and conversion.`,
  social: `Social Media Management:\n• Monthly content planning\n• Captions, creatives, scheduling\n• Community handling + growth strategy\n• Analytics & reporting\n\nIdeal for consistent brand presence.`,
  pricing: `Pricing is fully custom and depends on scope, features, timelines, and support needs.\n\nShare your requirement and we will provide a tailored quote after a quick discussion.`,
  contact: `Reach us directly:\nEmail: hello@webdigiting.com\nPhone: +91 94757 08366\n\nOr submit the contact form here. We usually respond within 24 hours.`,
  process: `Our process:\n1) Discovery call (goals + scope)\n2) Proposal + timeline\n3) Execution (design/dev/marketing)\n4) Review + QA\n5) Launch + optimization support`,
  timeline: `Estimated turnaround:\n• Landing page: 1-2 weeks\n• Website: 2-8 weeks\n• SEO/Marketing setup: 1-2 weeks, then ongoing\n• App MVP: 6-14 weeks\n\nExact timeline depends on required features and revisions.`,
  support: `Yes, we provide post-launch support:\n• Bug fixes and maintenance\n• Performance tuning\n• Content updates\n• Ongoing marketing/SEO iterations`,
  portfolio: `We have delivered 200+ projects across websites, apps, branding, and growth campaigns. If you share your niche, we can suggest similar case-study style examples during consultation.`,
  about: `WebDigiting is a full-service digital agency focused on growth outcomes. We combine strategy, design, development, and marketing to help businesses scale with measurable results.`,
  default: `I can help with services, pricing, timelines, process, and contact details. Try asking:\n• "Best service for lead generation?"\n• "How much for website?"\n• "Project timeline?"`
};

const QR_MAP = {
  services:['Web Dev','SEO','Branding','Pricing'],
  web:['App Development','Pricing','Contact Us'],
  app:['Web Development','Pricing','Contact Us'],
  seo:['Digital Marketing','Pricing','Contact Us'],
  marketing:['SEO Optimization','Pricing','Contact Us'],
  design:['Branding','Pricing','Contact Us'],
  branding:['UI/UX Design','Video Editing','Pricing'],
  video:['Social Media','Pricing','Contact Us'],
  social:['Branding','Digital Marketing','Pricing'],
  pricing:['Contact Us','Our Process','Services'],
  contact:['Services','Pricing','Our Process'],
  process:['Services','Pricing','Contact Us'],
  timeline:['Pricing','Our Process','Contact Us'],
  support:['Our Process','Pricing','Contact Us'],
  portfolio:['Services','Contact Us','Pricing'],
  about:['Services','Our Process','Contact Us'],
  default:['Services','Pricing','Contact Us']
};

const QR_ALIAS = {
  'web dev':'web','seo':'seo','branding':'branding','pricing':'pricing','contact us':'contact',
  'our process':'process','services':'services','social media':'social','app development':'app',
  'ui/ux design':'design','seo optimization':'seo','digital marketing':'marketing',
  'video editing':'video','web development':'web'
};

const classify = (text) => {
  const t = text.toLowerCase();
  if (/\b(services?|what do you (do|offer)|offer)\b/.test(t)) return 'services';
  if (/\b(web|website)\b/.test(t)) return 'web';
  if (/\b(app|mobile|ios|android)\b/.test(t)) return 'app';
  if (/\b(seo|search engine|rank|organic)\b/.test(t)) return 'seo';
  if (/\b(marketing|campaign|ads|paid|ppc)\b/.test(t)) return 'marketing';
  if (/\b(design|ui|ux|interface)\b/.test(t)) return 'design';
  if (/\b(brand|branding|logo|identity)\b/.test(t)) return 'branding';
  if (/\b(video|editing|reel|film)\b/.test(t)) return 'video';
  if (/\b(social|instagram|facebook|tiktok)\b/.test(t)) return 'social';
  if (/\b(price|pricing|cost|how much|quote)\b/.test(t)) return 'pricing';
  if (/\b(contact|email|phone|call|reach)\b/.test(t)) return 'contact';
  if (/\b(process|how|steps?|workflow)\b/.test(t)) return 'process';
  if (/\b(timeline|time|duration|how long|delivery)\b/.test(t)) return 'timeline';
  if (/\b(support|maintenance|after launch|help)\b/.test(t)) return 'support';
  if (/\b(portfolio|work|case study|projects?)\b/.test(t)) return 'portfolio';
  if (/\b(about|who are|team|company)\b/.test(t)) return 'about';
  return 'default';
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const msgsRef = useRef(null);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, typing]);

  const openChat = () => {
    setOpen(true);
    if (!initialized) {
      setTimeout(() => {
        setMessages([{ role:'bot', text:"👋 Hi! I'm <strong>Digi</strong>, WebDigiting's AI assistant. How can I help you today?" }]);
        setQuickReplies(['Our Services','Pricing','Our Process','Contact Us']);
        setInitialized(true);
      }, 300);
    }
  };

  const sendMessage = (text) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { role:'user', text }]);
    setInput(''); setQuickReplies([]); setTyping(true);
    setTimeout(() => {
      const key = QR_ALIAS[text.toLowerCase()] || classify(text);
      setTyping(false);
      setMessages(prev => [...prev, { role:'bot', text: KB[key] }]);
      setQuickReplies(QR_MAP[key] || QR_MAP.default);
    }, 900 + Math.random() * 500);
  };

  return (
    <>
      {/* FAB */}
      <button className="chatbot-fab" onClick={() => open ? setOpen(false) : openChat()}
        style={{ position:'fixed', bottom:28, right:28, zIndex:1000, width:56, height:56, borderRadius:'50%',
          background:'var(--accent)', border:'none', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 4px 24px rgba(245,200,0,0.35)', transition:'transform 0.3s' }}>
        <span style={{ position:'absolute', top:0, right:0, width:13, height:13, borderRadius:'50%', background:'#22c55e', border:'2px solid var(--black)' }} />
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:22,height:22}}>
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:24,height:24}}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
      </button>

      {/* Chat Window */}
      <div className="chatbot-window" style={{ position:'fixed', bottom:96, right:28, zIndex:999, width:'90vw', maxWidth:360,
        background:'#13120a', border:'1px solid var(--border)', borderRadius:12,
        display:'flex', flexDirection:'column', maxHeight:520,
        boxShadow:'0 20px 60px rgba(0,0,0,0.6)',
        transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        opacity: open ? 1 : 0, pointerEvents: open ? 'all' : 'none',
        visibility: open ? 'visible' : 'hidden',
        contentVisibility: open ? 'visible' : 'hidden',
        transition:'transform 0.3s ease, opacity 0.3s ease, visibility 0.3s ease' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderBottom:'1px solid var(--border-soft)', flexShrink:0 }}>
          <div style={{ width:34, height:34, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}>
              <circle cx="12" cy="8" r="4"/><path d="M4 20v-1a8 8 0 0 1 16 0v1"/>
            </svg>
          </div>
          <div style={{ flex:1 }}>
            <p style={{ fontSize:13, fontWeight:500, color:'var(--white)' }}>Digi — WebDigiting AI</p>
            <p style={{ fontSize:11, color:'#22c55e', display:'flex', alignItems:'center', gap:4, marginTop:1 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:'#22c55e', display:'inline-block' }}/>Online now
            </p>
          </div>
          <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', color:'var(--muted-light)', cursor:'pointer', display:'flex' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:15,height:15}} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Messages */}
        <div ref={msgsRef} style={{ flex:1, overflowY:'auto', padding:14, display:'flex', flexDirection:'column', gap:10, minHeight:0 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display:'flex', gap:7, alignItems:'flex-end', maxWidth:'85%', alignSelf: m.role==='bot'?'flex-start':'flex-end', flexDirection: m.role==='bot'?'row':'row-reverse' }}>
              {m.role==='bot' && (
                <div style={{ width:24, height:24, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:11,height:11}}><circle cx="12" cy="8" r="4"/><path d="M4 20v-1a8 8 0 0 1 16 0v1"/></svg>
                </div>
              )}
              <div style={{ padding:'9px 13px', borderRadius:10, fontSize:13, lineHeight:1.6,
                background: m.role==='bot' ? 'rgba(255,255,255,0.06)' : 'var(--accent)',
                color: m.role==='bot' ? 'var(--white)' : 'var(--black)',
                borderBottomLeftRadius: m.role==='bot' ? 2 : 10,
                borderBottomRightRadius: m.role==='user' ? 2 : 10 }}
                dangerouslySetInnerHTML={{ __html: m.text.replace(/\n/g,'<br>') }} />
            </div>
          ))}
          {typing && (
            <div style={{ display:'flex', gap:7, alignItems:'flex-end', alignSelf:'flex-start' }}>
              <div style={{ width:24, height:24, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:11,height:11}}><circle cx="12" cy="8" r="4"/><path d="M4 20v-1a8 8 0 0 1 16 0v1"/></svg>
              </div>
              <div style={{ display:'flex', gap:4, padding:'10px 14px', background:'rgba(255,255,255,0.06)', borderRadius:10, borderBottomLeftRadius:2 }}>
                {[0,1,2].map(i=>(
                  <span key={i} style={{ width:6, height:6, borderRadius:'50%', background:'var(--muted-light)', display:'inline-block', animation:`typing 1.2s ${i*0.2}s infinite` }}/>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick replies */}
        {quickReplies.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:'0 12px 8px' }}>
            {quickReplies.map(qr => (
              <button key={qr} onClick={() => sendMessage(qr)}
                style={{ background:'none', border:'1px solid var(--border)', color:'var(--muted-light)', padding:'4px 11px', borderRadius:20, fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif', transition:'all 0.2s' }}
                onMouseEnter={e=>{e.target.style.borderColor='var(--accent)';e.target.style.color='var(--accent)'}}
                onMouseLeave={e=>{e.target.style.borderColor='var(--border)';e.target.style.color='var(--muted-light)'}}>
                {qr}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ display:'flex', gap:8, padding:'10px 12px', borderTop:'1px solid var(--border-soft)', flexShrink:0 }}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendMessage(input)}
            placeholder="Ask me anything…"
            style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(245,200,0,0.15)', color:'var(--white)', padding:'8px 13px', fontSize:13, fontFamily:'DM Sans,sans-serif', borderRadius:20, outline:'none' }} />
          <button onClick={()=>sendMessage(input)}
            style={{ width:34, height:34, borderRadius:'50%', background:'var(--accent)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.2s' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--black)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}>
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
      <style>{`@keyframes typing{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-5px);opacity:1}}`}</style>
    </>
  );
};

export default Chatbot;
